import { useState } from "react";
import { useTranslation } from "react-i18next";

import AddIcon from "@mui/icons-material/Add";
import { Button, Stack, Typography } from "@mui/material";

import { nameof } from "../../../../utils/nameof";
import { ClientContent } from "../../../client-content/ClientContent";
import { useClientFileContext } from "../../../context/ClientFileContext";
import { AssetRelativesFormValues, DependantFormValue } from "./RelativesView.types";
import { AddDependantDialog } from "./components/add-dependant-dialog/AddDependantDialog";
import { DependantsBlock } from "./components/dependants-block/DependantsBlock";
import { MaintenanceDocuments } from "./components/maintenance-documents/MaintenanceDocuments";
import { RelativesNoticeBox } from "./components/relatives-notice-box/RelativesNoticeBox";
import { useAssetRelatives } from "./hooks/useAssetRelatives";

export function RelativesView() {
  const { clientId } = useClientFileContext();
  const { t } = useTranslation();
  const { formik, missingFields, saveAttempted, onAddMove, onCancelMove } = useAssetRelatives(clientId);

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);

  const fieldName = nameof<AssetRelativesFormValues>("dependants");

  const onAddDependant = (dependant: DependantFormValue) => {
    void formik.setFieldValue(fieldName, [...formik.values.dependants, dependant]);
  };

  const onRemoveDependant = (index: number) => {
    const next = formik.values.dependants.filter((_, i) => i !== index);
    void formik.setFieldValue(fieldName, next);
  };

  const documentsFieldName = nameof<AssetRelativesFormValues>("documents");

  const onDropDocuments = (files: File[]) => {
    void formik.setFieldValue(documentsFieldName, [...formik.values.documents, ...files]);
  };

  const onRemovePendingDocument = (index: number) => {
    const next = formik.values.documents.filter((_, i) => i !== index);
    void formik.setFieldValue(documentsFieldName, next);
  };

  const documentsToDeleteFieldName = nameof<AssetRelativesFormValues>("documentsToDelete");

  const onToggleDeleteDocument = (documentId: string) => {
    const current = formik.values.documentsToDelete;
    const next = current.includes(documentId) ? current.filter((id) => id !== documentId) : [...current, documentId];
    void formik.setFieldValue(documentsToDeleteFieldName, next);
  };

  return (
    <ClientContent formik={formik} missingFields={missingFields}>
      <Stack sx={{ bgcolor: "background.paper", p: 2, gap: 3 }}>
        <Typography variant="h6">{t("clients.client.debtSettlement.assetInventory.relatives")}</Typography>
        <RelativesNoticeBox />
        <DependantsBlock formik={formik} livesInHousehold onRemove={onRemoveDependant} />
        <DependantsBlock formik={formik} livesInHousehold={false} onRemove={onRemoveDependant} />
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => setIsAddDialogOpen(true)}
          sx={{ alignSelf: "flex-end" }}
        >
          {t("clients.client.debtSettlement.assetInventory.dependants.addDependant")}
        </Button>
        <MaintenanceDocuments
          clientId={clientId}
          pendingFiles={formik.values.documents}
          documentsToDelete={formik.values.documentsToDelete}
          documentMoves={formik.values.documentMoves}
          requireDocuments={
            saveAttempted && formik.values.dependants.some((dependant) => dependant.isEntitledToMaintenance === true)
          }
          onDropFiles={onDropDocuments}
          onRemovePending={onRemovePendingDocument}
          onToggleDelete={onToggleDeleteDocument}
          onAddMove={onAddMove}
          onCancelMove={onCancelMove}
        />
        {isAddDialogOpen && <AddDependantDialog onClose={() => setIsAddDialogOpen(false)} onAdd={onAddDependant} />}
      </Stack>
    </ClientContent>
  );
}
